import { useEffect, useRef, useState } from 'react';
import { useMap } from '@vis.gl/react-google-maps';
import { MarkerClusterer, Cluster, MarkerUtils } from "@googlemaps/markerclusterer";
import { useReportBounds } from "../lib/BoundsRegistry";
function buildImage(imgSrc) {
    if (imgSrc) {
        const imgElem = document.createElement('img');
        imgElem.src = imgSrc;
        return imgElem;
    }
    return null;
}
function areAllMarkersAtSamePosition(markers) {
    let allAtSamePos = true;
    let firstPos = null;
    for (let i = 0; i < markers.length; i++) {
        const markerPos = MarkerUtils.getPosition(markers[i]);
        if (firstPos == null) {
            firstPos = markerPos;
        }
        else if (markerPos && (markerPos.lat() !== firstPos.lat() || markerPos.lng() !== firstPos.lng())) {
            allAtSamePos = false;
            break;
        }
    }
    return allAtSamePos;
}
export function MarkerCluster({ markerProps, onClusterClicked }) {
    const map = useMap();
    const clustererRef = useRef(undefined);
    const [markers, setMarkers] = useState([]);
    // MarkerClusterer captures onClusterClick once at construction, so mirror `markers`
    // into a ref it can read from without needing to be recreated on every data change.
    const markersRef = useRef(markers);
    markersRef.current = markers;
    useReportBounds(markerProps.map(m => m.position));
    const buildMarker = (map, props) => {
        const element = new google.maps.marker.AdvancedMarkerElement({
            map,
            title: props.title,
            position: props.position,
            zIndex: props.zIndex,
            content: buildImage(props.imageUri)
        });
        element.addListener("click", () => {
            props.onClick && props.onClick(props, element);
        });
        return { props, element };
    };
    const onClusterClick = (event, cluster) => {
        const allMarkersAtSamePos = areAllMarkersAtSamePosition(cluster.markers ?? []);
        if (map && !allMarkersAtSamePos) {
            const clusterBounds = cluster.bounds;
            if (clusterBounds) {
                map.fitBounds(clusterBounds);
                map.setCenter(clusterBounds.getCenter());
            }
        }
        else if (onClusterClicked) {
            const currentMarkers = markersRef.current
                .filter(marker => cluster.markers?.includes(marker.element))
                .map(marker => marker.props);
            onClusterClicked(cluster, currentMarkers);
        }
    };
    useEffect(() => {
        if (!map) {
            return;
        }
        const built = markerProps.map(m => buildMarker(map, m));
        setMarkers(built);
        clustererRef.current = new MarkerClusterer({
            map,
            markers: built.map(m => m.element),
            onClusterClick
        });
        return () => {
            clustererRef.current?.setMap(null);
            clustererRef.current = undefined;
            built.forEach(m => m.element.map = null);
            setMarkers([]);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map]);
    useEffect(() => {
        const clusterer = clustererRef.current;
        if (!map || !clusterer) {
            return;
        }
        const removedMarkers = [];
        const retainedMarkers = [];
        markers.forEach(markerInfo => {
            if (markerProps.some(m => m.key === markerInfo.props.key)) {
                retainedMarkers.push(markerInfo);
            }
            else {
                removedMarkers.push(markerInfo.element);
                markerInfo.element.map = null;
            }
        });
        if (removedMarkers.length) {
            clusterer.removeMarkers(removedMarkers, true);
        }
        const addedMarkers = [];
        markerProps.forEach(m => {
            if (!retainedMarkers.some(markerInfo => markerInfo.props.key === m.key)) {
                addedMarkers.push(buildMarker(map, m));
            }
        });
        if (addedMarkers.length) {
            clusterer.addMarkers(addedMarkers.map(m => m.element), true);
        }
        if (addedMarkers.length || removedMarkers.length) {
            setMarkers([...retainedMarkers, ...addedMarkers]);
        }
        clusterer.render();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [markerProps]);
    return null;
}
